import { Users } from 'lucide-react';

import { FeeReceipts } from '@/components/fees/FeeReceipts';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import { parentApi } from '../api/parent.api';
import { useParentChild, useSelectedChild } from '../store/child.store';
import { PARENT_PALETTE } from '../theme';

/**
 * Fees, for the parent of the child on screen.
 *
 * Reads `GET /parent/children/:uuid/fees`, which is the office's own fee
 * ledger trimmed for a family — the same read the child's panel makes — and
 * draws it with the same `FeeReceipts`. What a parent sees here is therefore
 * exactly what the counter sees, down to the receipt numbers.
 *
 * Read-only. There is no payment route in this product, so there is no "Pay
 * now": a button promising one would be a promise the school cannot keep.
 *
 * The child is never in the address. It comes from the switcher in the sidebar,
 * and the server resolves which children the caller may see from their own
 * token — an id in the URL is an id somebody can change (CLAUDE.md §10).
 */
export function ParentFeesPage() {
  const child = useSelectedChild();
  const isLoadingChildren = useParentChild((state) => state.isLoading);
  const childrenError = useParentChild((state) => state.error);
  const reloadChildren = useParentChild((state) => state.load);

  const { data, isLoading, error, refetch } = useApiQuery(
    () => (child ? parentApi.fees(child.uuid) : Promise.resolve(null)),
    [child?.uuid],
  );

  // Switching children refetches; until the new ledger lands, the old one
  // belongs to somebody else and must not be drawn under this child's name.
  const account = data && data.child.uuid === child?.uuid ? data : null;

  if (isLoadingChildren) {
    return (
      <div className={cn(PARENT_PALETTE, 'flex flex-col gap-6')}>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 rounded-3xl" />
      </div>
    );
  }

  if (childrenError) {
    return (
      <div className={cn(PARENT_PALETTE)}>
        <QueryError
          title="Could not load your children"
          error={new ApiRequestError(childrenError, 0, 'INTERNAL_ERROR')}
          onRetry={() => void reloadChildren()}
        />
      </div>
    );
  }

  if (!child) {
    return (
      <div className={cn(PARENT_PALETTE, 'card')}>
        <EmptyState
          icon={<Users className="h-5 w-5" />}
          title="No child linked to your account yet"
          description="Your school office links a parent's account to their child's record. Once that is done, your child's fees and receipts appear here."
        />
      </div>
    );
  }

  return (
    <div className={cn(PARENT_PALETTE, 'flex flex-col gap-5')}>
      <header className="min-w-0">
        <h1 className="text-2xl font-bold tracking-tight text-ink">Fees</h1>
        <p className="mt-1 text-sm text-ink-2">
          What has been charged for {child.fullName} in {child.className}
          {account ? ` for ${account.academicYear.name}` : ''}, and every payment recorded.
        </p>
      </header>

      <FeeReceipts
        account={account}
        isLoading={isLoading || (!account && !error)}
        error={error}
        onRetry={refetch}
      />
    </div>
  );
}
